<template>
  <section :class="['quota-editor space-y-2.5', compact && 'is-compact']">
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0 flex-1">
        <div class="form-label flex items-baseline gap-1">
          <span>{{ title }}</span>
          <span class="text-red-500">*</span>
        </div>
        <div class="mt-1 flex items-baseline gap-1.5 text-xs">
          <span class="text-slate-400">合计</span>
          <span
            class="font-semibold tabular-nums"
            :class="totalAmount > 0 && !quotaTotalMatched(localQuotas, targetAmount) ? 'text-amber-600' : 'text-slate-900'"
          >¥{{ formatAmount(totalAmount) }}</span>
          <span class="text-slate-300">/</span>
          <span class="font-medium tabular-nums text-slate-500">¥{{ formatAmount(targetAmount) }}</span>
        </div>
      </div>
      <button
        type="button"
        class="btn-secondary inline-flex items-center gap-1 px-2.5 py-1 text-xs"
        :disabled="disabled || !canAddRow"
        @click="addQuotaRow"
      >
        <PlusIcon class="h-3.5 w-3.5" />
        新增
      </button>
    </div>

    <div
      class="quota-editor__progress"
      role="progressbar"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-valuenow="progressPercent"
      :aria-label="`已分配 ${progressPercent}%`"
    >
      <div
        class="quota-editor__progress-fill"
        :class="quotaTotalMatched(localQuotas, targetAmount) ? 'is-matched' : 'is-pending'"
        :style="{ width: `${progressPercent}%` }"
      />
    </div>

    <div class="space-y-1.5">
      <div
        v-for="(quota, index) in localQuotas"
        :key="quota.localId"
        class="grid grid-cols-1 items-start gap-2 sm:grid-cols-[minmax(0,1fr)_120px_auto]"
      >
        <el-select
          v-model="quota.productNames"
          multiple
          collapse-tags
          collapse-tags-tooltip
          :max-collapse-tags="compact ? 1 : 2"
          filterable
          :reserve-keyword="false"
          :persistent="true"
          class="form-select form-control-sm w-full"
          :disabled="disabled"
          placeholder="选择品名"
          @change="emitChange"
        >
          <template #header>
            <div class="flex items-center justify-between gap-2 border-b border-slate-100 px-2 py-1.5">
              <span class="text-xs text-slate-500">已选 {{ quota.productNames.length }} 项</span>
              <div class="flex items-center gap-0.5">
                <button
                  type="button"
                  class="rounded px-1.5 py-0.5 text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:text-slate-300"
                  :disabled="availableProductNames(index).length === 0"
                  @mousedown.prevent.stop
                  @click.stop="selectAvailableProductNames(index)"
                >
                  全选可用
                </button>
                <button
                  type="button"
                  class="rounded px-1.5 py-0.5 text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50"
                  @mousedown.prevent.stop
                  @click.stop="selectDefaultProductNames(index)"
                >
                  默认
                </button>
              </div>
            </div>
          </template>
          <el-option
            v-for="name in ENTERPRISE_INVOICE_PRODUCT_NAMES"
            :key="name"
            :label="name"
            :value="name"
            :disabled="isProductNameDisabled(name, index)"
          >
            <div class="flex items-center justify-between gap-2">
              <span>{{ name }}</span>
              <CheckIcon
                v-if="quota.productNames.includes(name)"
                class="h-3.5 w-3.5 text-primary-600"
              />
            </div>
          </el-option>
        </el-select>

        <input
          v-model="quota.displayAmount"
          type="text"
          inputmode="decimal"
          class="form-input form-control-sm w-full text-right tabular-nums"
          :disabled="disabled"
          placeholder="0"
          @input="emitChange"
          @blur="normalizeDisplayAmount(index)"
        >

        <button
          type="button"
          class="table-action-button is-danger justify-self-end sm:mt-0.5 sm:justify-self-auto"
          title="删除"
          :disabled="disabled || localQuotas.length <= 1"
          @click="removeQuotaRow(index)"
        >
          <TrashIcon class="h-3.5 w-3.5" />
        </button>
      </div>
    </div>

    <div
      :class="[
        'quota-editor__status flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs',
        quotaTotalMatched(localQuotas, targetAmount) ? 'is-matched' : 'is-pending'
      ]"
    >
      <CheckCircleIcon v-if="quotaTotalMatched(localQuotas, targetAmount)" class="h-3.5 w-3.5 flex-shrink-0" />
      <ExclamationCircleIcon v-else class="h-3.5 w-3.5 flex-shrink-0" />
      <span v-if="quotaTotalMatched(localQuotas, targetAmount)">金额合计已匹配目标额度</span>
      <span v-else-if="differenceAmount > 0">金额合计需等于目标额度，还需分配 ¥{{ formatAmount(differenceAmount) }}</span>
      <span v-else>金额合计需等于目标额度，超出 ¥{{ formatAmount(Math.abs(differenceAmount)) }}</span>
    </div>
  </section>
</template>

<script setup>
import { computed, reactive, watch } from 'vue'
import {
  CheckIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/vue/24/outline'
import {
  DEFAULT_ENTERPRISE_INVOICE_PRODUCT_NAMES,
  ENTERPRISE_INVOICE_PRODUCT_NAMES
} from '../productNames'
import {
  hasSameProductQuotaDrafts,
  normalizeProductQuotaDrafts,
  normalizeQuotaAmount,
  quotaTotalAmount,
  quotaTotalMatched
} from '../productQuota'

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  targetAmount: { type: Number, default: 0 },
  amountStep: { type: Number, default: 100 },
  title: { type: String, required: true },
  disabled: { type: Boolean, default: false },
  compact: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue'])

const localQuotas = reactive([])
let nextLocalId = 1
let isEmittingLocalChange = false

const totalAmount = computed(() => quotaTotalAmount(localQuotas))
const canAddRow = computed(() => usedProductNames().size < ENTERPRISE_INVOICE_PRODUCT_NAMES.length)

const progressPercent = computed(() => {
  const target = normalizeQuotaAmount(props.targetAmount)
  if (target <= 0) return 0
  const total = quotaTotalAmount(localQuotas)
  return Math.min(100, Math.round((total / target) * 100))
})

const differenceAmount = computed(() => {
  const target = normalizeQuotaAmount(props.targetAmount)
  const total = quotaTotalAmount(localQuotas)
  return target - total
})

function resetLocalQuotas(previousTargetAmount = null) {
  const incomingQuotas = normalizeProductQuotaDrafts(props.modelValue, props.targetAmount)
  if (hasSameProductQuotaDrafts(incomingQuotas, modelValueFromLocalQuotas())) {
    if (applyDefaultTargetAmount({ previousTargetAmount, defaultEmpty: false })) {
      syncLocalQuotasToModel()
    }
    return
  }

  localQuotas.splice(0, localQuotas.length)
  for (const quota of incomingQuotas) {
    localQuotas.push({
      localId: nextLocalId++,
      productNames: [...quota.productNames],
      displayAmount: quota.displayAmount
    })
  }
  applyDefaultTargetAmount({ previousTargetAmount, defaultEmpty: true })
  syncLocalQuotasToModel()
}

function applyDefaultTargetAmount({ previousTargetAmount = null, defaultEmpty = true } = {}) {
  const targetAmount = normalizeQuotaAmount(props.targetAmount)
  if (targetAmount <= 0 || localQuotas.length !== 1) return false
  const onlyQuota = localQuotas[0]
  const currentAmount = normalizeQuotaAmount(onlyQuota.displayAmount)
  const hasPreviousTarget = previousTargetAmount !== null && previousTargetAmount !== undefined
  const previousAmount = normalizeQuotaAmount(previousTargetAmount)
  const targetChanged = hasPreviousTarget && previousAmount !== targetAmount
  if ((defaultEmpty && currentAmount <= 0) || (targetChanged && currentAmount === previousAmount)) {
    onlyQuota.displayAmount = targetAmount
    return true
  }
  return false
}

function modelValueFromLocalQuotas() {
  return localQuotas.map(quota => ({
    productNames: [...quota.productNames],
    displayAmount: normalizeQuotaAmount(quota.displayAmount)
  }))
}

function syncLocalQuotasToModel() {
  const nextValue = modelValueFromLocalQuotas()
  if (!hasSameProductQuotaDrafts(props.modelValue, nextValue)) {
    isEmittingLocalChange = true
    emit('update:modelValue', nextValue)
  }
}

function emitChange() {
  isEmittingLocalChange = true
  emit('update:modelValue', modelValueFromLocalQuotas())
}

function normalizeDisplayAmount(index) {
  const quota = localQuotas[index]
  if (!quota) return
  quota.displayAmount = normalizeQuotaAmount(quota.displayAmount)
  emitChange()
}

function addQuotaRow() {
  const used = usedProductNames()
  const firstUnused = ENTERPRISE_INVOICE_PRODUCT_NAMES.find(name => !used.has(name))
  if (!firstUnused) return
  localQuotas.push({
    localId: nextLocalId++,
    productNames: [firstUnused],
    displayAmount: 0
  })
  emitChange()
}

function removeQuotaRow(index) {
  if (localQuotas.length <= 1) return
  localQuotas.splice(index, 1)
  emitChange()
}

function usedProductNames(ignoreIndex = -1) {
  const used = new Set()
  localQuotas.forEach((quota, index) => {
    if (index === ignoreIndex) return
    for (const name of quota.productNames || []) used.add(name)
  })
  return used
}

function availableProductNames(index) {
  const used = usedProductNames(index)
  return ENTERPRISE_INVOICE_PRODUCT_NAMES.filter(name => !used.has(name))
}

function isProductNameDisabled(name, index) {
  return usedProductNames(index).has(name)
}

function updateQuotaProductNames(index, productNames) {
  const quota = localQuotas[index]
  if (!quota) return
  quota.productNames = productNames
  emitChange()
}

function selectAvailableProductNames(index) {
  updateQuotaProductNames(index, availableProductNames(index))
}

function selectDefaultProductNames(index) {
  const available = new Set(availableProductNames(index))
  const defaults = DEFAULT_ENTERPRISE_INVOICE_PRODUCT_NAMES.filter(name => available.has(name))
  updateQuotaProductNames(index, defaults.length > 0 ? defaults : availableProductNames(index).slice(0, 1))
}

function formatAmount(value) {
  const amount = normalizeQuotaAmount(value)
  return amount.toLocaleString('zh-CN', { maximumFractionDigits: 0 })
}

watch(
  () => [props.modelValue, props.targetAmount],
  (value, oldValue) => {
    if (isEmittingLocalChange && hasSameProductQuotaDrafts(props.modelValue, modelValueFromLocalQuotas())) {
      isEmittingLocalChange = false
      return
    }
    isEmittingLocalChange = false
    resetLocalQuotas(oldValue?.[1] ?? null)
  },
  { immediate: true, deep: true }
)
</script>

<style scoped>
.quota-editor__progress {
  height: 4px;
  border-radius: 9999px;
  background: rgb(241 245 249);
  overflow: hidden;
}

.quota-editor__progress-fill {
  height: 100%;
  border-radius: 9999px;
  transition: width 0.25s ease, background-color 0.25s ease;
}

.quota-editor__progress-fill.is-pending {
  background: rgb(245 158 11);
}

.quota-editor__progress-fill.is-matched {
  background: rgb(16 185 129);
}

.quota-editor__status.is-matched {
  background: rgb(236 253 245);
  color: rgb(4 120 87);
}

.quota-editor__status.is-pending {
  background: rgb(255 251 235);
  color: rgb(180 83 9);
}

@media (prefers-reduced-motion: reduce) {
  .quota-editor__progress-fill {
    transition: none;
  }
}
</style>
